/**
 * In-memory mock of the calibration-runs REST surface.
 *
 * Every call resolves after a short randomized delay so the UI exercises its
 * loading states, and matches the exact return shapes of the corresponding
 * function in `api/real-api.ts` / `api/metrics-api.ts` / `api/environments-api.ts`
 * — `api/api.ts` dispatches between the two per-call, so callers never know
 * which one ran.
 */

import type { AISUser } from '@frontend-platform/user-directory';
import type { BotEnvironment } from '../api/environments-api';
import type { CalibrationMetric, CalibrationRun } from '../lib/types';
import { mockStore } from './mock-store';

function delay(ms = 150 + Math.random() * 250): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function mockListCalibrationRuns(): Promise<CalibrationRun[]> {
  await delay();
  return mockStore.listRuns();
}

export async function mockGetCalibrationRun<T = unknown>(runId: string): Promise<T> {
  await delay();
  const detail = mockStore.getRunDetail(runId);
  if (detail) return detail as unknown as T;
  // No seeded detail for this run (e.g. a freshly created or non-completed
  // one) — fall back to the list entry so the page still renders something
  // instead of throwing, same as a run whose detail payload hasn't landed yet.
  const fromList = mockStore.listRuns().find((r) => r.runId === runId);
  if (!fromList) throw new Error(`Mock run not found: ${runId}`);
  return fromList as unknown as T;
}

export async function mockCreateCalibrationRun(
  payload: Record<string, unknown>,
  context: { env: string; botRefId?: string }
): Promise<unknown> {
  await delay();
  return mockStore.createRun(payload, context);
}

export async function mockDeleteCalibrationRun(id: string): Promise<void> {
  await delay();
  mockStore.deleteRun(id);
}

export async function mockCancelCalibrationRun(id: string): Promise<void> {
  await delay();
  mockStore.cancelRun(id);
}

export interface MockRunEventStatus {
  requestId?: string;
  status?: string;
}

export async function mockGetRunEventStatus(): Promise<MockRunEventStatus | null> {
  await delay();
  // Nothing in a real backend is behind this mock, so the poll itself drives
  // the pipeline forward: a pre-run status advances to IN_PROGRESS, and an
  // IN_PROGRESS run eventually completes — otherwise either would hold the
  // bot's single run slot forever.
  mockStore.advanceMockPipeline();
  const active = mockStore.listRuns().find((r) => r.status === 'IN_PROGRESS');
  if (!active) return null;
  return { requestId: active.runId, status: active.status };
}

// Mock mode has no real S3 object to presign — hand the raw URL straight back.
export async function mockGetPresignedReportDownloadUrl(rawUrl: string): Promise<string> {
  await delay();
  return rawUrl;
}

export async function mockGetConversationCount(): Promise<number | null> {
  await delay();
  return 500 + Math.floor(Math.random() * 500);
}

export async function mockGetLiveConversationCount(): Promise<number | null> {
  await delay();
  return 50 + Math.floor(Math.random() * 200);
}

export async function mockResolveUsers(ids: string[]): Promise<Map<string, AISUser>> {
  await delay();
  const users = mockStore.users();
  const result = new Map<string, AISUser>();
  ids.forEach((id, i) => {
    const found = mockStore
      .listRuns()
      .flatMap((r) => [r.createdBy, r.updatedBy])
      .find((u) => u?.id === id);
    result.set(id, found ?? (users[i % users.length] as AISUser));
  });
  return result;
}

export async function mockFetchAllMetrics(): Promise<CalibrationMetric[]> {
  await delay();
  return mockStore.listMetrics();
}

export async function mockCreateMetric(
  payload: Record<string, unknown>
): Promise<CalibrationMetric> {
  await delay();
  return mockStore.createMetric(payload);
}

export async function mockUpdateMetric(
  id: string,
  payload: Record<string, unknown>
): Promise<CalibrationMetric> {
  await delay();
  return mockStore.updateMetric(id, payload);
}

export async function mockDeleteMetric(id: string): Promise<void> {
  await delay();
  mockStore.deleteMetric(id);
}

export async function mockGetBotEnvironments(): Promise<BotEnvironment[]> {
  await delay();
  return mockStore.listEnvironments();
}
